import EventUtils from '@rio-cloud/rio-uikit/EventUtils';
import { Map, Marker, Route, SingleMapMarker } from 'components/map/Map';
import type { BoundingBox, MapApi, MapEvent, Position } from 'components/map/types';
import { getMapCredentials } from 'configuration';
import { useMemo, useRef } from 'react';

import type { HereRoute } from './hereRouting';
import { simplifyToMax } from './interpolation';
import { isPlaced, type PlacedWaypoint, type Waypoint } from './types';

const mapCredentials = getMapCredentials();
const DEFAULT_CENTER: Position = { lat: 48.03, lng: 11.7 };
// Keeps the map responsive for large pasted waypoint sets
const MAX_MARKERS = 100;
// A tap right after a marker drag is part of the drag, not a new waypoint
const TAP_AFTER_DRAG_GUARD_MS = 200;

// HERE's global namespace, loaded by the RIO Map. Only panning is toggled so the RIO behavior defaults stay intact.
const getPanningFeature = () => (window as any).H.mapevents.Behavior.Feature.PANNING;

const getMarkerColor = (index: number, total: number) => {
    if (index === 0) return 'bg-map-marker-success';
    if (index === total - 1) return 'bg-map-marker-danger';
    return 'bg-map-marker-route';
};

type WaypointMarkerProps = {
    api: MapApi;
    waypoint: PlacedWaypoint;
    index: number;
    total: number;
    lastDragEndAt: { current: number };
    onUpdate: (id: string, position: Position) => void;
};

const WaypointMarker = ({ api, waypoint, index, total, lastDragEndAt, onUpdate }: WaypointMarkerProps) => {
    const eventListenerMap = useMemo(
        () => ({
            [EventUtils.DRAG_START]: () => api.behavior && api.behavior.disable(getPanningFeature()),
            [EventUtils.DRAG]: (event: MapEvent, enhanced: any) => {
                event.target.setGeometry(enhanced.calculateGeoCoords());
            },
            [EventUtils.DRAG_END]: (event: MapEvent) => {
                if (api.behavior) api.behavior.enable(getPanningFeature());
                lastDragEndAt.current = Date.now();
                const { lat, lng } = event.target.getGeometry();
                onUpdate(waypoint.id, { lat, lng });
            },
        }),
        [api, waypoint.id, lastDragEndAt, onUpdate]
    );

    return (
        <Marker
            position={waypoint.position}
            draggable
            icon={<SingleMapMarker markerColor={getMarkerColor(index, total)} name={String(index + 1)} />}
            eventListenerMap={eventListenerMap}
        />
    );
};

type RouteBuilderMapProps = {
    waypoints: Waypoint[];
    route: HereRoute | undefined;
    fitBounds: BoundingBox | undefined;
    onAdd: (position: Position) => void;
    onUpdate: (id: string, position: Position) => void;
};

const RouteBuilderMap = ({ waypoints, route, fitBounds, onAdd, onUpdate }: RouteBuilderMapProps) => {
    const lastDragEndAt = useRef(0);

    const mapEventListenerMap = useMemo(
        () => ({
            [EventUtils.TAP]: (event: MapEvent, enhanced: any) => {
                if (EventUtils.isRightClick(event)) return;
                if (Date.now() - lastDragEndAt.current < TAP_AFTER_DRAG_GUARD_MS) return;
                const { lat, lng } = enhanced.calculateGeoCoords();
                onAdd({ lat, lng });
            },
        }),
        [onAdd]
    );

    // Indices refer to the full waypoint list so marker numbers match the rows in the sidebar
    const markers = useMemo(() => {
        const placed = waypoints.flatMap((waypoint, index) => (isPlaced(waypoint) ? [{ waypoint, index }] : []));
        if (placed.length <= MAX_MARKERS) return placed;
        const kept = simplifyToMax(
            placed.map(({ waypoint }) => waypoint.position),
            MAX_MARKERS
        );
        return placed.filter((_, i) => kept.has(i));
    }, [waypoints]);

    return (
        <div className='size-full'>
            <Map
                credentials={mapCredentials}
                center={DEFAULT_CENTER}
                zoom={10}
                boundingBox={fitBounds}
                eventListenerMap={mapEventListenerMap}
                hideMapSettings
            >
                {(api: MapApi) => (
                    <>
                        {markers.map(({ waypoint, index }) => (
                            <WaypointMarker
                                key={waypoint.id}
                                api={api}
                                waypoint={waypoint}
                                index={index}
                                total={waypoints.length}
                                lastDragEndAt={lastDragEndAt}
                                onUpdate={onUpdate}
                            />
                        ))}
                        {route && (
                            <Route
                                positions={route.positions}
                                startIcon={<SingleMapMarker iconNames={['start']} markerColor='bg-map-marker-route' />}
                                endIcon={<SingleMapMarker iconNames={['finish']} markerColor='bg-map-marker-route' />}
                                hasArrows={false}
                                style={{
                                    width: 5,
                                    borderWidth: 2,
                                    isBorderIncludedInWidth: false,
                                    color: '#328092',
                                    borderColor: '#255F6C',
                                }}
                            />
                        )}
                    </>
                )}
            </Map>
        </div>
    );
};

export default RouteBuilderMap;
